
import React, { useState } from 'react';
import Button from './Button';
import { User } from '../types';
import { findUserByEmail, saveUserToDB } from '../services/dbService';
import { sanitizeError } from '../services/geminiService';

interface AuthFormProps {
  onSuccess: (user: User) => void;
  onBack: () => void;
  t: any;
  uiLang: string;
}

const AuthForm: React.FC<AuthFormProps> = ({ onSuccess, onBack, t, uiLang }) => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (isLogin) {
        const user = await findUserByEmail(email);
        if (user && user.password === password) {
          onSuccess(user);
        } else {
          setError(uiLang === 'ar' ? "البريد الإلكتروني أو كلمة المرور غير صحيحة." : "Email ou mot de passe incorrect.");
        }
      } else {
        const existing = await findUserByEmail(email);
        if (existing) {
          setError(uiLang === 'ar' ? "هذا البريد الإلكتروني مستخدم بالفعل." : "Cet email est déjà utilisé.");
        } else {
          const newUser: User = {
            id: Date.now().toString(),
            username,
            email,
            password,
            avatarColor: ['#4f46e5', '#10b981', '#f59e0b', '#ef4444'][Math.floor(Math.random() * 4)],
            joinedAt: Date.now()
          };
          await saveUserToDB(newUser);
          onSuccess(newUser);
        }
      }
    } catch (err: any) {
      // Affichage de l'erreur réelle pour le débogage (ex: RLS Policy error)
      const rawMsg = err?.message || "Une erreur est survenue lors de la connexion.";
      setError(sanitizeError(rawMsg));
      console.error("Auth Error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[#fdfbf7] flex items-center justify-center p-6 z-50 animate-fadeIn">
      <div className="max-w-md w-full bg-white rounded-[3rem] shadow-2xl p-8 md:p-12 border border-indigo-50 relative">
        <button onClick={onBack} className={`absolute top-8 ${uiLang === 'ar' ? 'right-8' : 'left-8'} text-indigo-300 hover:text-indigo-600 transition-colors`}>
          <svg className={uiLang === 'ar' ? 'rotate-180' : ''} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
        </button>

        <div className="text-center space-y-4 mb-8">
          <div className="w-16 h-16 bg-indigo-600 rounded-2xl flex items-center justify-center text-white text-3xl font-bold shadow-lg shadow-indigo-200 mx-auto">ⵣ</div>
          <h2 className="text-3xl font-bold text-indigo-950">{isLogin ? t.loginTitle : t.signupTitle}</h2>
          <p className="text-indigo-400 text-sm">{t.authDesc}</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <label className="text-xs font-bold text-indigo-900 uppercase mx-2">{t.name}</label>
              <input 
                required 
                type="text" 
                value={username} 
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-4 bg-indigo-50/50 border border-transparent focus:border-indigo-200 focus:bg-white rounded-2xl outline-none transition-all"
                placeholder="..."
              />
            </div>
          )}
          <div className="space-y-2">
            <label className="text-xs font-bold text-indigo-900 uppercase mx-2">{t.email}</label>
            <input 
              required 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              className="w-full p-4 bg-indigo-50/50 border border-transparent focus:border-indigo-200 focus:bg-white rounded-2xl outline-none transition-all text-left"
              placeholder="votre@email.com"
              dir="ltr"
            />
          </div>
          <div className="space-y-2">
            <label className="text-xs font-bold text-indigo-900 uppercase mx-2">{t.password}</label>
            <input 
              required 
              type="password" 
              value={password} 
              onChange={(e) => setPassword(e.target.value)}
              className="w-full p-4 bg-indigo-50/50 border border-transparent focus:border-indigo-200 focus:bg-white rounded-2xl outline-none transition-all text-left"
              placeholder="••••••••"
              dir="ltr"
            />
          </div>

          {error && <p className="text-red-500 text-xs font-bold text-center bg-red-50 p-3 rounded-xl break-words">{error}</p>}

          <Button isLoading={loading} className="w-full py-4 rounded-2xl mt-4">
            {isLogin ? t.loginBtn : t.signupBtn}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <button 
            onClick={() => setIsLogin(!isLogin)}
            className="text-indigo-600 font-bold text-sm hover:underline"
          >
            {isLogin ? t.noAccount : t.hasAccount}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;
